import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/user/Layout';
import { Award, Gift, Clock, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

interface Reward {
  id: string; 
  name: string;
  description: string;
  points_required: number;
  photo_url?: string;
  language?: string;
  translations?: Record<string, any>;
  target_countries?: string[];
}

interface RewardRequest {
  id: string;
  reward_id: string;
  reward_name: string;
  points: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

const Rewards: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentLanguage } = useLanguage();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [rewardRequests, setRewardRequests] = useState<RewardRequest[]>([]);
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'catalog' | 'history'>('catalog');
  const [userProfile, setUserProfile] = useState<{ country?: string, total_points?: number } | null>(null);

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      fetchRewardRequests();
    }
  }, [user]);

  useEffect(() => {
    if (user && userProfile) {
      fetchRewards();
    }
  }, [user, currentLanguage, userProfile]);

  const fetchUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('country, total_points')
        .eq('id', user?.id)
        .single();
      
      if (error) throw error;
      setUserProfile(data);
      setUserPoints(data?.total_points || 0);
      console.log('User profile fetched:', data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchRewards = async () => {
    try {
      setLoading(true);
      
      // Set language for RLS policies
      await supabase.rpc('set_language', { lang: currentLanguage });
      
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .eq('active', true)
        .order('points_required', { ascending: true });
      
      if (error) throw error;
      
      if (data) {
        console.log('Rewards fetched:', data);
        console.log('User country:', userProfile?.country);
        
        // Client-side filtering for country targeting
        const filteredRewards = data.filter(reward => {
          // If no target countries, show to everyone
          if (!reward.target_countries || reward.target_countries.length === 0) {
            return true;
          }
          
          // If user has no country, don't show country-specific rewards
          if (!userProfile?.country) {
            return false;
          }
          
          // Check if user's country is in the target list (case insensitive)
          return reward.target_countries.some(
            country => country.toUpperCase() === userProfile.country?.toUpperCase()
          );
        });
        
        console.log('Filtered rewards:', filteredRewards);
        
        const processedRewards = filteredRewards.map(reward => {
          if (reward.language === currentLanguage) {
            return reward;
          } else if (reward.translations && reward.translations[currentLanguage]) {
            return {
              ...reward,
              name: reward.translations[currentLanguage].name,
              description: reward.translations[currentLanguage].description
            };
          }
          return reward;
        });
        
        setRewards(processedRewards);
      }
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast.error(t('errorFetchingRewards'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRewardRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('reward_requests')
        .select('*, rewards(name)')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      if (data) {
        const formattedRequests = data.map(request => ({
          id: request.id,
          reward_id: request.reward_id,
          reward_name: request.rewards.name,
          points: request.points,
          status: request.status,
          created_at: request.created_at
        }));
        
        setRewardRequests(formattedRequests);
      }
    } catch (error) {
      console.error('Error fetching reward requests:', error);
    }
  };

  const requestReward = async (reward: Reward) => {
    if (!user) {
      toast.error(t('pleaseLogin'));
      return;
    }

    if (userPoints < reward.points_required) {
      toast.error(t('notEnoughPoints'));
      return;
    }

    // Check if user already has a pending request for this reward
    const existingRequest = rewardRequests.some(
      req => req.reward_id === reward.id && req.status === 'pending'
    );
    
    if (existingRequest) {
      toast.error(t('alreadyRequestedReward'));
      return;
    }
    
    try {
      setRequestLoading(reward.id);

      // Create reward request
      const { error: requestError } = await supabase
        .from('reward_requests')
        .insert([
          {
            user_id: user.id,
            reward_id: reward.id,
            points: reward.points_required,
            status: 'pending'
          }
        ]);
      
      if (requestError) throw requestError;

      // Deduct points from user
      const { error: pointsError } = await supabase
        .from('user_points')
        .insert([
          {
            user_id: user.id,
            points: -reward.points_required,
            source: 'reward_request',
            description: `Requested reward: ${reward.name}`
          }
        ]);
      
      if (pointsError) throw pointsError;
      
      toast.success(t('rewardRequested'));
      fetchRewardRequests();
      fetchUserProfile(); // Update user points from profile
    } catch (error) {
      console.error('Error requesting reward:', error);
      toast.error(t('errorRequestingReward'));
    } finally {
      setRequestLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            {t('pending')}
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Check className="h-3 w-3 mr-1" />
            {t('approved')}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <X className="h-3 w-3 mr-1" />
            {t('rejected')}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <Layout title={t('rewards')}>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">{t('yourPoints')}</h3>
          <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-bold flex items-center">
            <Award className="h-5 w-5 mr-2" />
            {userPoints} {t('points')}
          </div>
        </div>
        
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('catalog')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'catalog'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('rewardsCatalog')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('rewardHistory')}
            </button>
          </nav>
        </div>
        
        {activeTab === 'catalog' ? (
          <div>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : rewards.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('noRewards')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {rewards.map((reward) => {
                  const hasPendingRequest = rewardRequests.some(
                    req => req.reward_id === reward.id && req.status === 'pending'
                  );

                  return (
                    <div key={reward.id} className="border rounded-lg overflow-hidden shadow-sm">
                      {reward.photo_url && (
                        <div className="relative h-48 bg-gray-100">
                          <img
                            src={reward.photo_url}
                            alt={reward.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://placehold.co/400x200/e2e8f0/64748b?text=No+Image';
                            }}
                          />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center">
                            {!reward.photo_url && <Gift className="h-10 w-10 text-blue-600 mr-3" />}
                            <div>
                              <h4 className="text-lg font-medium text-gray-900">{reward.name}</h4>
                              <p className="text-sm text-gray-500">{reward.description}</p>
                            </div>
                          </div>
                          <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm font-medium">
                            {reward.points_required} {t('points')}
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <button
                            onClick={() => requestReward(reward)}
                            disabled={
                              userPoints < reward.points_required || 
                              requestLoading === reward.id ||
                              hasPendingRequest
                            }
                            className={`w-full py-2 px-4 rounded-md ${
                              hasPendingRequest
                                ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed'
                                : userPoints >= reward.points_required
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            } transition-colors disabled:opacity-50`}
                          >
                            {hasPendingRequest
                              ? t('pendingRequest')
                              : requestLoading === reward.id
                              ? t('requesting')
                              : userPoints >= reward.points_required
                              ? t('requestReward')
                              : t('notEnoughPoints')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div>
            {rewardRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('noRewardRequests')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('reward')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('points')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('requestDate')}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rewardRequests.map((request) => (
                      <tr key={request.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{request.reward_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{request.points}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDate(request.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(request.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Rewards;